/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { Component } from "react";
import { Flex, Icon } from "@chakra-ui/react";
import PropTypes from "prop-types";
import Tooltip from "rc-tooltip";
import url from "url";
import clsx from "clsx";

import SafeImage from "@/js/components/SafeImage";
import { CloseButton } from "@/js/components/shared/CloseButton";
import WebsiteOrSignatureListModal from "@/js/components/WebsiteOrSignatureListModal";
import useStore from "@/stores/index";
import { trimString, extractHostname, platformIcon } from "../utils";
import { RestrictedAppsModal } from "./RestrictedAppsModal";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";

export default class FastWindowScreenItem extends Component {
	constructor(props) {
		super(props);
		this.state = {
			unblockOpen: false,
			infoOpen: false,
			listModalOpen: false,
			restrictedAppsModalOpen: false,
			isCurrentlyBlocked: useClassroomPolicyStore
				.getState()
				.isCurrentlyBlocked(extractHostname(this.props.windowData.url), this.props.user.username),
		};
		this.unsubClassroomPolicyStore = () => {};
	}

	componentDidMount() {
		this.unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			(newState) => {
				this.onChange(newState);
			}
		);
	}

	componentWillUnmount() {
		this.unsubClassroomPolicyStore();
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onChange = (state) => {
		this.setState({
			isCurrentlyBlocked: useClassroomPolicyStore
				.getState()
				.isCurrentlyBlocked(extractHostname(this.props.windowData.url), this.props.user.username),
		});
	};

	renderTabs() {
		let tabs = [];
		if (this.props.windowData.background_tabs) {
			tabs = this.props.windowData.background_tabs.map((tab, i) => (
				<SafeImage
					key={tab.tabUrl + i}
					styleClassName={clsx(this.props.isRestricted ? "tab-favicon--noInteract" : "tab-favicon clickable")}
					title={tab.title}
					src={tab.favIcon}
					alt={""}
					onClick={() =>
						this.openInfoModal(
							tab.tabUrl,
							tab.title,
							undefined,
							tab.tab_id,
							false,
							this.props.isMonitorMode,
							this.props.windowData,
							i,
							false,
							this.props.isRestricted
						)
					}
				/>
			));
		}
		if (tabs.length > this.props.tabItemLimit) {
			const ret = tabs.slice(0, this.props.tabItemLimit);
			const additionalTabs = Math.min(tabs.length - this.props.tabItemLimit, 99);
			const c = clsx("tab-favicon tab-favicon--hidden-tabs clickable", additionalTabs > 9 && "tab-favicon--small-text");
			ret.push(
				<SafeImage
					key="hidden-tabs-icon"
					styleClassName={c}
					title={tabs.length - this.props.tabItemLimit + " hidden items"}
					src={""}
					alt={"+" + String(additionalTabs)}
					onClick={() => this.openListModal("Open Tabs", this.props.windowData.background_tabs, this.props.isMonitorMode)}
				/>
			);
			return ret;
		} else {
			return tabs;
		}
	}

	renderFastConnectionItems() {
		const conns = [];
		const specialConns = [];
		if (this.props.fastconnItems) {
			for (const item of this.props.fastconnItems) {
				let signatureObj = this.props.signatureDb.find(item.tag);
				if (!signatureObj) {
					signatureObj = {
						name: item.item,
						id: item.category,
						url: item.website,
						favicon_url: undefined,
					};
				}
				specialConns.push(signatureObj);
				const favIcon = signatureObj.favicon_url;
				conns.push(
					<SafeImage
						key={signatureObj.name}
						styleClassName={"tab-favicon clickable"}
						title={signatureObj.name}
						src={favIcon}
						timestamp={item.timestamp}
						alt={signatureObj.name.charAt(0)}
						onClick={() =>
							this.openInfoModal(
								undefined,
								signatureObj.name,
								item.tag,
								item.tab_id,
								false,
								this.props.isMonitorMode,
								undefined,
								undefined,
								true,
								this.props.isRestricted
							)
						}
					/>
				);
			}
		}
		// Sort icons first then timestamp
		conns.sort(function (a, b) {
			if (a.props.src !== "" && b.props.src === "") {
				return -1;
			}
			if (a.props.src === "" && b.props.src !== "") {
				return 1;
			}
			return b.props.timestamp - a.props.timestamp;
		});

		if (conns.length > this.props.tabItemLimit) {
			const ret = conns.slice(0, this.props.tabItemLimit);
			const additionalConnections = Math.min(conns.length - this.props.tabItemLimit, 99);
			const c = clsx("tab-favicon tab-favicon--hidden-tabs clickable", additionalConnections > 9 && "tab-favicon--small-text");
			ret.push(
				<SafeImage
					key={"hidden-items-icon"}
					styleClassName={c}
					title={conns.length - this.props.tabItemLimit + " hidden items"}
					src={""}
					alt={"+" + String(additionalConnections)}
					onClick={() => this.openListModal("Connections", specialConns, this.props.isMonitorMode)}
				/>
			);
			return ret;
		} else {
			return conns;
		}
	}

	/**
	 * Opens the Live View modal
	 * @param {*} url
	 * @param {*} title
	 * @param {*} tag
	 * @param {*} tabId
	 * @param {boolean} isActiveTab
	 * @param {*} isMonitorMode
	 * @param {*} screen
	 * @param {*} index
	 * @param {boolean} isFastconns
	 * @param {boolean} isRestricted - Is the teacher currently restricted from seeing the screen based on the domain or app in view? (via Screen Visibility Restrictions)
	 * @returns {void}
	 */
	openInfoModal = (
		url,
		title,
		tag,
		tabId,
		isActiveTab = false,
		isMonitorMode,
		screen,
		index,
		isFastconns = false,
		isRestricted = false
	) => {
		if (isRestricted) return;

		if (!isFastconns && this.props.openP2pModal) {
			this.props.openP2pModal(this.props.user, tabId, screen.chrome_id, screen.chrome_window_id, isActiveTab);
			return;
		}

		this.setState(
			{
				infoModal_url: url,
				infoModal_title: title,
				infoModal_tag: tag,
				infoModal_tabId: tabId,
				infoModal_isActiveTab: isActiveTab,
				infoModal_isMonitorMode: isMonitorMode,
				infoModal_tabIndex: index,
			},
			() =>
				this.setState({
					infoOpen: true,
				})
		);
	};

	/**
	 * Opens the list modal
	 * @param {string} title
	 * @param {*} items
	 * @param {boolean} isMonitorMode
	 * @returns {void}
	 */
	openListModal = (title, items, isMonitorMode) => {
		this.setState(
			{
				listModalTitle: title,
				listModal_items: items,
				listModalIsMonitorMode: isMonitorMode,
			},
			() => this.setState({ listModalOpen: true })
		);
	};

	closeListModal = () => {
		this.setState({ listModalOpen: false });
	};

	/**
	 * @returns {React.JSX.Element}
	 */
	renderScreen = () => {
		if (this.props.isScreenshotLoading) {
			return <div className="RegularClassroomUser_screenshotLoading">Loading new screenshot...</div>;
		} else {
			return this.props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
				<div
					className={clsx("RegularClassroomUser_screenshotContainer", !this.props.isRestricted && "clickable")}
					onClick={() =>
						this.openInfoModal(
							this.props.windowData.url,
							this.props.windowData.title,
							undefined,
							this.props.windowData.tab_id,
							true,
							this.props.isMonitorMode,
							this.props.windowData,
							-1,
							false,
							this.props.isRestricted
						)
					}
				>
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(this.props.windowData.title, 40)}
						src={this.props.windowData.screenshot}
					/>
				</div>
			) : (
				<div className="RegularClassroomUser_screenshotContainer">
					<img
						className="RegularClassroomUser_screenshotImage"
						alt={trimString(this.props.windowData.title, 40)}
						src={this.props.windowData.screenshot}
					/>
				</div>
			);
		}
	};

	disableCloseTab = () => {
		return this.props.isScreenshotLoading || !this.props.windowData.tab_id || !this.props.classIsActive;
	};

	closeShowInfo = () => {
		this.setState({
			infoOpen: false,
		});
	};

	render() {
		const specialConns = [];
		if (this.props.fastconnItems) {
			for (const item of this.props.fastconnItems) {
				let signatureObj = this.props.signatureDb.find(item.tag);
				if (!signatureObj) {
					signatureObj = {
						name: item.item,
						id: item.category,
						url: item.website,
						favicon_url: undefined,
					};
				}
				specialConns.push(signatureObj);
			}
		}

		//We need to parse the url string into URL object in JS
		//This is to make sure we only have parsable URL with proper protocol opened in a new tab.
		const extractedUrl = url.parse(this.props.windowData.url);

		const platformIconClassname = platformIcon(this.props.windowData);
		return (
			<>
				<div className="RegularClassroomUser_screenHeader">
					{this.props.inScreenshare && (
						<Flex mr={4} width={"24px"} flexShrink={0}>
							<Icon className="fa-solid fa-screencast" boxSize={5} color={"#0075DB"} />
						</Flex>
					)}
					{this.props.windowData.url !== "" || useStore.getState().featureToggles.data["agent-p2p-screenshots"] ? (
						this.props.isRestricted ? (
							<RestrictedAppsModal
								screenshotData={this.props.windowData}
								isMonitorMode={this.props.isMonitorMode}
								isClassActive={this.props.classIsActive}
								forceCloseTab={this.props.forceCloseTab}
								canCloseTab={this.props.canCloseTab}
								classIsActive={this.props.classIsActive}
								signatures={this.props.signatureDb}
								student={this.props.user}
							/>
						) : (
							<Tooltip
								placement="bottomLeft"
								trigger={extractedUrl.protocol ? ["hover"] : []}
								destroyTooltipOnHide={true}
								overlayClassName={"Tooltip_OpenNewTabHyperlink"}
								overlay={
									<a href={extractedUrl.href} target="_blank" rel="noopener noreferrer">
										Open in new tab
									</a>
								}
							>
								<span
									className={clsx("RegularClassroomUser_screenHeaderText", !this.props.isRestricted && "clickable")}
									title={this.props.windowData.title}
									data-testid="button-live-view-modal"
									onClick={() =>
										this.openInfoModal(
											this.props.windowData.url,
											this.props.windowData.title,
											undefined,
											this.props.windowData.tab_id,
											true,
											this.props.isMonitorMode,
											this.props.windowData,
											-1,
											false,
											this.props.isRestricted
										)
									}
								>
									{!this.props.isScreenshotLoading && this.props.windowData.title}
								</span>
							</Tooltip>
						)
					) : (
						<span className={"RegularClassroomUser_screenHeaderText"} title={this.props.windowData.title}>
							{!this.props.isScreenshotLoading && this.props.windowData.title}
						</span>
					)}
					{this.props.windowData.url !== "" && !this.props.isRestricted && (
						<Flex marginLeft={"auto"}>
							{this.props.canCloseTab && (
								<CloseButton
									tooltipText="Close Tab"
									tooltipId={this.props.windowData.tab_id + "_closeTabTooltip"}
									disabled={this.disableCloseTab()}
									onClick={() => {
										this.props.forceCloseTab(this.props.windowData.tab_id, true);
									}}
								/>
							)}
						</Flex>
					)}
				</div>
				{this.renderScreen()}
				<WebsiteOrSignatureListModal
					title={this.state.listModalTitle}
					open={this.state.listModalOpen}
					activeTab={this.props.windowData}
					items={this.state.listModal_items}
					signatureDb={this.props.signatureDb}
					user={this.props.user}
					onClose={this.closeListModal}
					forceCloseTab={this.props.forceCloseTab}
					canCloseTab={this.props.canCloseTab}
					classIsActive={this.props.classIsActive}
					isMonitorMode={this.state.listModalIsMonitorMode}
				/>
				<div className="RegularClassroomUser_tabRows">
					{this.props.windowData.background_tabs && this.props.windowData.background_tabs.length > 0 && (
						<div className="RegularClassroomUser_tabRow">
							<button
								data-testid="button-open-info-modal"
								className="RegularClassroomUser_tabsAgentIcon"
								onClick={() => this.openListModal("Open Tabs", this.props.windowData.background_tabs, this.props.isMonitorMode)}
							>
								<i className={platformIconClassname} aria-hidden="true" />
							</button>
							{this.renderTabs()}
						</div>
					)}
					{this.props.fastconnItems && this.props.fastconnItems.length > 0 && (
						<div className={"RegularClassroomUser_tabRow"}>
							<span className="RegularClassroomUser_tabsAgentIcon">
								<i
									className="fa fa-wifi"
									aria-hidden="true"
									onClick={() => this.openListModal("Connections", specialConns, this.props.isMonitorMode)}
								/>
							</span>
							{this.renderFastConnectionItems()}
						</div>
					)}
				</div>
			</>
		);
	}
}

FastWindowScreenItem.propTypes = {
	user: PropTypes.object,
	windowData: PropTypes.object,
	isScreenshotLoading: PropTypes.bool,
	fastconnItems: PropTypes.array,
	isMonitorMode: PropTypes.bool,
	forceCloseTab: PropTypes.func,
	classIsActive: PropTypes.bool,
	canCloseTab: PropTypes.bool,
	signatureDb: PropTypes.object,
	openP2pModal: PropTypes.func,
	tabItemLimit: PropTypes.number,
	/**
	 * Is the teacher currently restricted from seeing the screen based on the domain or app in view? (via Screen Visibility Restrictions)
	 */
	isRestricted: PropTypes.bool,
	inScreenshare: PropTypes.bool,
};
